<template>
  <div class="container-fluid py-4">
    <div class="d-flex justify-content-between align-items-center mb-4 header-clinique flex-wrap gap-2">
      <div class="d-flex align-items-center gap-3">
        <i :class="['bi', icone, 'fs-2 text-primary']"></i>
        <div>
          <h2 class="fw-bold mb-0">{{ titre }}</h2>
          <small class="text-muted">{{ total.toLocaleString('fr-FR') }} {{ unite }}</small>
        </div>
      </div>
      <div class="d-flex gap-2 flex-wrap">
        <slot name="boutons" :lignes="lignes"></slot>
        <button v-if="peutCreer" class="btn btn-primary shadow-sm btn-clinique" @click="$emit('creer')">
          <i class="bi bi-plus-circle me-1"></i>{{ libelleCreer }}
        </button>
      </div>
    </div>

    <div class="card p-3 mb-3 shadow-sm rounded card-clinique">
      <div class="row g-2 align-items-end">
        <div class="col-12 col-md-4">
          <label class="form-label fw-semibold small mb-1">Rechercher</label>
          <input v-model="recherche" type="search" class="form-control form-control-clinique" :placeholder="placeholder">
        </div>
        <div v-if="filtreActif" class="col-6 col-md-2">
          <label class="form-label fw-semibold small mb-1">Statut</label>
          <select v-model="actif" class="form-select form-select-clinique">
            <option value="">Tous</option>
            <option value="1">Activés</option>
            <option value="0">Désactivés</option>
          </select>
        </div>
        <slot name="filtres"></slot>
        <div v-if="Object.keys(optionsTri).length > 1" class="col-6 col-md-2">
          <label class="form-label fw-semibold small mb-1">Trier par</label>
          <select v-model="tri" class="form-select form-select-clinique">
            <option v-for="(colonne, libelle) in optionsTri" :key="colonne" :value="colonne">{{ libelle }}</option>
          </select>
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label fw-semibold small mb-1">Ordre</label>
          <select v-model="sens" class="form-select form-select-clinique">
            <option value="asc">Croissant</option>
            <option value="desc">Décroissant</option>
          </select>
        </div>
      </div>
    </div>

    <div v-if="erreur" class="alert alert-danger d-flex justify-content-between align-items-center">
      <span>{{ erreur }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="charger">Réessayer</button>
    </div>

    <div class="card shadow-sm rounded card-clinique">
      <div class="table-responsive" :class="{ 'opacity-50': chargement }">
        <table class="table align-middle text-center mb-0 table-hover modern-table-clinique w-100">
          <thead>
            <tr>
              <th>#</th>
              <slot name="entete"></slot>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(l, i) in lignes" :key="l.id">
              <td class="text-muted">{{ rang(i) }}</td>
              <slot name="ligne" :ligne="l" :index="i"></slot>
            </tr>
            <tr v-if="!chargement && !lignes.length">
              <td :colspan="colonnes" class="text-center text-muted py-4">{{ filtresActifs ? 'Aucun résultat pour cette recherche.' : vide }}</td>
            </tr>
            <tr v-if="chargement && !lignes.length">
              <td :colspan="colonnes" class="text-center text-muted py-4"><span class="spinner-border spinner-border-sm me-2"></span>Chargement…</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="px-3 pb-3">
        <Pagination v-model:page="page" v-model:par-page="parPage" :total="total" />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Liste d'un référentiel (types, services, chambres…) paginée par le serveur :
 * recherche, filtre Activés / Désactivés, tri, pagination.
 *
 *   <ListeReferentiel ref="liste" titre="Types de service" ressource="type_services"
 *       tri-defaut="ts.nom" :options-tri="{ Nom: 'ts.nom' }" :colonnes="5" …>
 *     <template #entete><th>Nom</th>…</template>
 *     <template #ligne="{ ligne }"><td>{{ ligne.nom }}</td>…</template>
 *   </ListeReferentiel>
 *
 * Filtres propres à l'écran : prop « filtres » (objet, recharge à chaque
 * changement) et slot « filtres » pour les champs. Après une action, l'écran
 * appelle this.$refs.liste.charger() ou this.$refs.liste.supprimer(...).
 */
import api, { messageErreur, notifier } from '@/api';
import Pagination from '@/components/commun/Pagination.vue';
import { listeServeur } from '@/components/commun/listeServeur';
import { confirmer } from '@/components/commun/dialogue';

export default {
  name: 'ListeReferentiel',
  components: { Pagination },
  mixins: [listeServeur(function () { return this.ressource; })],
  props: {
    titre: { type: String, required: true },
    icone: { type: String, default: 'bi-list-ul' },
    ressource: { type: String, required: true },
    triDefaut: { type: String, required: true },
    sensDefaut: { type: String, default: 'asc' },
    /** { libellé: colonne } — colonnes autorisées par le serveur */
    optionsTri: { type: Object, default: () => ({}) },
    placeholder: { type: String, default: 'Nom…' },
    unite: { type: String, default: 'élément(s)' },
    vide: { type: String, default: 'Aucun élément enregistré.' },
    colonnes: { type: Number, default: 4 },
    peutCreer: { type: Boolean, default: false },
    libelleCreer: { type: String, default: 'Ajouter' },
    filtreActif: { type: Boolean, default: true },
    filtres: { type: Object, default: () => ({}) },
  },
  emits: ['creer'],
  data() {
    return { tri: this.triDefaut, sens: this.sensDefaut, actif: '' };
  },
  computed: {
    filtresActifs() {
      return !!(this.recherche.trim() || this.actif !== '' || Object.values(this.filtres).some(v => v !== '' && v !== null && v !== undefined));
    },
  },
  watch: {
    actif() { this.rechargerListe(); },
    filtres: { deep: true, handler() { this.rechargerListe(); } },
  },
  methods: {
    filtresListe() {
      return { actif: this.actif, ...this.filtres };
    },
    /** Demande confirmation, supprime, prévient et recharge. */
    async supprimer({ id, titre = 'Supprimer', message = 'Supprimer cet élément ?', succes = 'Élément supprimé.', ressource = this.ressource }) {
      const ok = await confirmer({ titre, message, libelleValider: 'Supprimer', danger: true });
      if (!ok) return false;
      try {
        await api.deleteItem(ressource, id);
        notifier(succes, 'success');
        this.charger();
        return true;
      } catch (e) {
        if (e?.response?.status !== 403) notifier(messageErreur(e, 'Suppression impossible.'), 'danger');
        return false;
      }
    },
  },
};
</script>

<style scoped>
.header-clinique { padding: 15px 20px; background-color: #e9f5ff; border-radius: 12px; }
.card-clinique { background: #f8fafd; border-radius: 12px; border: none; }
.form-control-clinique, .form-select-clinique { border-radius: 10px; border: 1px solid #d1e3f0; }
.form-control-clinique:focus, .form-select-clinique:focus { box-shadow: 0 0 6px #0d6efd33; border-color: #0d6efd; }
.btn-clinique { background: linear-gradient(90deg, #0d6efd, #6610f2); border: none; color: #fff; border-radius: 10px; }
.btn-clinique:hover { box-shadow: 0 5px 15px rgba(0,0,0,0.2); }
.modern-table-clinique thead { background: linear-gradient(90deg, #8fd3f4, #0d6efd); color: #fff; }
.modern-table-clinique thead :deep(th) { background: transparent; color: #fff; font-weight: 600; }
</style>
